import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ZAPIER_CONFIG, ZAPIER_EXPORT } from 'constants/constants';
import { ZapierConfig, ZapierExportResponse, ExportStatus } from './types';

interface UseZapierExportReturn {
  isConfigured: boolean;
  isLoading: boolean;
  exportStatus: ExportStatus;
  exportMessage: string | null;
  queuedCount: number;
  handleExport: (documentIds: string[]) => Promise<void>;
  resetExport: () => void;
  checkConfig: () => Promise<void>;
}

export const useZapierExport = (): UseZapierExportReturn => {
  const [isConfigured, setIsConfigured] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [exportStatus, setExportStatus] = useState<ExportStatus>('idle');
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  const [queuedCount, setQueuedCount] = useState(0);

  const checkConfig = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get<{ data: ZapierConfig }>(ZAPIER_CONFIG);
      const config = response.data.data;
      setIsConfigured(config.export_enabled);
    } catch (error) {
      console.error('Failed to check Zapier configuration:', error);
      setIsConfigured(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkConfig();
  }, [checkConfig]);

  const resetExport = useCallback(() => {
    setExportStatus('idle');
    setExportMessage(null);
    setQueuedCount(0);
  }, []);

  const handleExport = useCallback(async (documentIds: string[]) => {
    if (documentIds.length === 0) {
      setExportStatus('error');
      setExportMessage('No documents selected for export');
      return;
    }

    setExportStatus('exporting');
    setExportMessage(null);

    try {
      const response = await axios.post<ZapierExportResponse>(ZAPIER_EXPORT, {
        document_ids: documentIds,
      });

      const { queued_count, message } = response.data.data;
      setQueuedCount(queued_count);
      setExportMessage(message);
      setExportStatus('success');
    } catch (error: any) {
      console.error('Zapier export failed:', error);
      const errorMessage =
        error.response?.data?.errors?.[0]?.message ||
        error.response?.data?.message ||
        'Failed to export to Zapier. Please try again.';
      setExportMessage(errorMessage);
      setExportStatus('error');
    }
  }, []);

  return {
    isConfigured,
    isLoading,
    exportStatus,
    exportMessage,
    queuedCount,
    handleExport,
    resetExport,
    checkConfig,
  };
};
